// Design System Toast Component
// Notification toast/snackbar component

import React, { forwardRef } from 'react';
import {
  Snackbar,
  Alert as MuiAlert,
  IconButton,
  Slide,
  styled,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { borderTokens, shadowTokens } from '../../tokens';

// Styled Alert component
const StyledAlert = styled(MuiAlert)(({ theme }) => ({
  borderRadius: borderTokens.md,
  boxShadow: shadowTokens.medium,
  alignItems: 'center',
  
  '& .MuiAlert-icon': {
    fontSize: '1.5rem',
  },
  
  '& .MuiAlert-message': {
    padding: '8px 0',
  },
}));

// Slide transition
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

// Alert component for use in Snackbar
const Alert = forwardRef((props, ref) => {
  return <StyledAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

Alert.displayName = 'Alert';

/**
 * Toast - Notification toast component
 * 
 * Features:
 * - Multiple severity levels (success, error, warning, info)
 * - Auto-hide with configurable duration
 * - Action buttons
 * - Dismissible
 * - Position control
 * 
 * @example
 * // Basic toast
 * <Toast 
 *   open={open} 
 *   message="Operation successful!" 
 *   severity="success"
 *   onClose={handleClose}
 * />
 * 
 * // With action
 * <Toast
 *   open={open}
 *   message="File uploaded"
 *   severity="info"
 *   action={
 *     <Button size="small" onClick={handleUndo}>
 *       UNDO
 *     </Button>
 *   }
 *   onClose={handleClose}
 * />
 */
const Toast = ({
  open,
  onClose,
  message,
  severity = 'info',
  duration = 6000,
  position = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  action,
  disableAutoHide = false,
  TransitionComponent = SlideTransition,
  variant = 'filled',
  onExited,
  ...props
}) => {
  // Handle close
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    onClose?.(event, reason);
  };
  
  return (
    <Snackbar
      open={open}
      autoHideDuration={disableAutoHide ? null : duration}
      onClose={handleClose}
      anchorOrigin={position}
      TransitionComponent={TransitionComponent}
      onExited={onExited}
      {...props}
    >
      <Alert
        severity={severity}
        variant={variant}
        onClose={handleClose}
        action={
          <>
            {action}
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

Toast.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  message: PropTypes.node.isRequired,
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  duration: PropTypes.number,
  position: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  action: PropTypes.node,
  disableAutoHide: PropTypes.bool,
  TransitionComponent: PropTypes.elementType,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  onExited: PropTypes.func,
};

/**
 * ToastManager - Hook for managing multiple toasts
 * 
 * @example
 * const { toasts, showToast, hideToast } = useToastManager();
 * 
 * // Show toast
 * showToast('Success!', 'success');
 * 
 * // Render toasts
 * {toasts.map(toast => (
 *   <Toast key={toast.id} {...toast} onClose={() => hideToast(toast.id)} />
 * ))}
 */
export const useToastManager = () => {
  const [toasts, setToasts] = React.useState([]);
  
  const showToast = React.useCallback((message, severity = 'info', options = {}) => {
    const id = Date.now();
    const toast = {
      id,
      open: true,
      message,
      severity,
      ...options,
    };
    
    setToasts(prev => [...prev, toast]);
    return id;
  }, []);
  
  const hideToast = React.useCallback((id) => {
    setToasts(prev => 
      prev.map(toast => 
        toast.id === id ? { ...toast, open: false } : toast
      )
    );
  }, []);
  
  const removeToast = React.useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  
  return {
    toasts,
    showToast,
    hideToast,
    removeToast,
  };
};

export default Toast;